import { useRef, useState } from 'react';
import { readReportStream } from '../utils/report-stream';
import { siteConfig } from '../config/site';

export function useStreamingReport() {
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState('');
  const [access, setAccess] = useState<'preview' | 'full' | null>(null);
  const [reportToken, setReportToken] = useState('');
  const busy = useRef(false);
  const run = async (url: string, init: RequestInit, onText: (text: string) => void) => {
    if (busy.current) throw new Error('报告正在生成，请稍候');
    busy.current = true;
    setStreaming(true);
    setError('');
    setAccess(null);
    setReportToken('');
    try {
      const headers = new Headers(init.headers);
      headers.set('Accept', 'text/event-stream');
      if (siteConfig.isPaidMode) headers.set('X-Report-Mode', 'paid');
      const body = typeof init.body === 'string' ? JSON.parse(init.body) : {};
      const requiresPreview = siteConfig.isPaidMode
        && ['/api/ziwei-career', '/api/vedic-report', '/api/talent-kid', '/api/hepan'].includes(url)
        && !body.validationOnly && !body.followUpQuestion?.trim();
      let previewConfirmed = false;
      let currentToken = '';
      let grantConfirmed = false;
      const response = await fetch(url, { ...init, headers });
      if (!response.ok) {
        setError('报告生成失败，请重试。');
        return response;
      }
      if (!response.headers.get('Content-Type')?.includes('text/event-stream') || !response.body) {
        if (requiresPreview) throw new Error('收费接口未确认预览模式，请更新服务端后重试。');
        return response;
      }
      const payload = await readReportStream(response.body, (text) => {
        if (requiresPreview && !previewConfirmed) throw new Error('收费接口未确认预览模式，已停止展示正文。');
        onText(text);
      }, (event) => {
        if (event.type === 'start' && typeof event.reportToken === 'string') {
          currentToken = event.reportToken;
          setReportToken(currentToken);
        }
        if (event.type === 'unlocked' && currentToken && event.reportToken === currentToken) grantConfirmed = true;
        if (requiresPreview && event.type === 'replace' && !grantConfirmed) throw new Error('完整正文尚未授权，已停止展示。');
        if (event.access === 'preview') previewConfirmed = true;
        if (requiresPreview && event.access === 'full' && !grantConfirmed) throw new Error('收费页面收到免费模式响应，已停止展示正文。');
        if (event.access === 'preview' || event.access === 'full') setAccess(event.access);
      });
      return new Response(JSON.stringify(payload), { status: 200, headers: { 'Content-Type': 'application/json' } });
    } catch (error) {
      setError('报告生成中断，当前内容可能不完整，请重新生成。');
      throw error;
    } finally {
      busy.current = false;
      setStreaming(false);
    }
  };
  return { run, streaming, error, access, reportToken };
}
